import React, { useCallback, useEffect, useRef } from "react";

interface ScalableVectorGraphicsProps
  extends React.SVGAttributes<SVGSVGElement> {
  src?: string;
  fallback?: string;
  evalScripts?: "always" | "once" | "never";
  alt?: string;
  onInject?: (svg: SVGSVGElement) => void;
}

function ScalableVectorGraphics({
  src,
  fallback,
  className,
  onInject,
  style,
  // accepted for API compatibility but not used
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  evalScripts: _evalScripts,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  alt: _alt,
  ...svgProps
}: ScalableVectorGraphicsProps) {
  const containerRef = useRef<HTMLSpanElement>(null);

  const applySvgProps = useCallback(
    (svgEl: SVGSVGElement) => {
      svgEl.setAttribute("class", className ?? "");

      // Apply inline styles to the injected svg to preserve previous behavior.
      const cssStyle = style as React.CSSProperties | undefined;
      if (cssStyle) {
        Object.entries(cssStyle).forEach(([key, value]) => {
          (svgEl.style as unknown as Record<string, string>)[key] =
            value == null ? "" : String(value);
        });
      }

      Object.entries(svgProps).forEach(([key, value]) => {
        if (
          value == null ||
          key === "children" ||
          key === "dangerouslySetInnerHTML" ||
          key === "suppressContentEditableWarning" ||
          key === "suppressHydrationWarning" ||
          key === "defaultChecked" ||
          key === "defaultValue" ||
          key === "ref" ||
          key === "key"
        ) {
          return;
        }

        // Function props (onClick, etc.) stay on wrapper for event bubbling.
        if (typeof value === "function") {
          return;
        }

        if (typeof value === "boolean") {
          if (value) {
            svgEl.setAttribute(key, "");
          } else {
            svgEl.removeAttribute(key);
          }
          return;
        }

        svgEl.setAttribute(key, String(value));
      });
    },
    [className, style, svgProps]
  );

  useEffect(() => {
    const url = src || fallback;
    const container = containerRef.current;
    if (!url || !container) return;

    let cancelled = false;

    const applyHtml = (html: string) => {
      if (cancelled || !container) return;
      container.innerHTML = html;
      const svgEl = container.querySelector("svg");
      if (svgEl) {
        applySvgProps(svgEl as SVGSVGElement);
        if (onInject) onInject(svgEl as SVGSVGElement);
      }
    };

    fetch(url)
      .then((res) => res.text())
      .then(applyHtml)
      .catch(() => {
        if (!cancelled && fallback && url !== fallback) {
          fetch(fallback)
            .then((r) => r.text())
            .then(applyHtml)
            .catch(() => {});
        }
      });

    return () => {
      cancelled = true;
    };
  }, [src, fallback, applySvgProps, onInject]);

  // Sync props that affect SVG rendering without re-fetching.
  useEffect(() => {
    if (!containerRef.current) return;
    const svgEl = containerRef.current.querySelector("svg");
    if (svgEl) applySvgProps(svgEl as SVGSVGElement);
  }, [applySvgProps]);

  return (
    <span
      ref={containerRef}
      {...(svgProps as React.HTMLAttributes<HTMLSpanElement>)}
    />
  );
}

export default ScalableVectorGraphics;
